import { Menu } from '@base-ui-components/react/menu';
import { css } from 'aphrodite';
import * as React from 'react';
import { BulletIcon } from '../../components/svgs/icons/BulletIcon';
import { CheckmarkIcon } from '../../components/svgs/icons/CheckmarkIcon';
import { ChevronDown } from '../../components/svgs/icons/ChevronDown';
import { ChevronRight } from '../../components/svgs/icons/ChevronRight';
import { MoreIcon } from '../../components/svgs/icons/MoreIcon';
import { brandSecondary, navigation } from '../../styles/colors';
import { s } from './styles';

/**
 * Api docs: https://base-ui.com/react/components/menu
 */
const Dropdown = Menu.Root;
Dropdown.displayName = 'Dropdown';

const DropdownGroup = Menu.Group;
DropdownGroup.displayName = 'DropdownGroup';

const DropdownRadioGroup = Menu.RadioGroup;
DropdownRadioGroup.displayName = 'DropdownRadioGroup';

const DropdownTrigger = Menu.Trigger;
DropdownTrigger.displayName = 'DropdownTrigger';

const DropdownSelectTrigger = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Trigger>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.Trigger
		ref={ref}
		data-slot='dropdown-menu-select-trigger'
		className={css(s.selectTrigger, ...styles)}
		{...props}
	>
		<span>{children}</span> <ChevronDown className={css(s.triggerChevron)} />
	</Menu.Trigger>
));
DropdownSelectTrigger.displayName = 'DropdownSelectTrigger';

const DropdownEllipsisTrigger = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Trigger>>
>(({ styles = [], ...props }, ref) => (
	<Menu.Trigger ref={ref} className={css(s.ellipsisTrigger, ...styles)} {...props}>
		<MoreIcon fillColor={navigation} />
	</Menu.Trigger>
));
DropdownEllipsisTrigger.displayName = 'DropdownEllipsisTrigger';

interface DropdownContentProps extends React.ComponentPropsWithoutRef<typeof Menu.Popup> {
	align?: Menu.Positioner.Props['align'];
	sideOffset?: Menu.Positioner.Props['sideOffset'];
}

const DropdownContent = React.forwardRef<HTMLDivElement, AphroditeStyled<DropdownContentProps>>(
	({ styles = [], align = 'center', sideOffset = 4, ...props }, ref) => (
		<Menu.Portal>
			<Menu.Positioner sideOffset={sideOffset} align={align} className={css(s.dropdownPositioner)}>
				<Menu.Popup ref={ref} className={css(s.dropdownPopup, ...styles)} {...props} />
			</Menu.Positioner>
		</Menu.Portal>
	)
);
DropdownContent.displayName = 'DropdownContent';

const DropdownItem = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Item>>
>(({ styles = [], ...props }, ref) => <Menu.Item ref={ref} className={css(s.menuItem, ...styles)} {...props} />);
DropdownItem.displayName = 'DropdownItem';

const DropdownSeparator = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Separator>>
>(({ styles = [], ...props }, ref) => <Menu.Separator ref={ref} className={css(s.separator, ...styles)} {...props} />);
DropdownSeparator.displayName = 'DropdownSeparator';

const DropdownGroupLabel = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.GroupLabel>>
>(({ styles = [], ...props }, ref) => (
	<Menu.GroupLabel ref={ref} className={css(s.groupLabel, ...styles)} {...props} />
));
DropdownGroupLabel.displayName = 'DropdownGroupLabel';

const DropdownCheckboxItem = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.CheckboxItem>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.CheckboxItem ref={ref} className={css(s.checkboxItem, ...styles)} {...props}>
		<div className={css(s.checkBoxItemCheckWrapper)}>
			<Menu.CheckboxItemIndicator className={css(s.checkBoxItemCheckIndicator)}>
				<CheckmarkIcon className={css(s.checkBoxItemCheck)} />
			</Menu.CheckboxItemIndicator>
		</div>
		<span>{children}</span>
	</Menu.CheckboxItem>
));
DropdownCheckboxItem.displayName = 'DropdownCheckboxItem';

const DropdownRadioItem = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.RadioItem>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.RadioItem ref={ref} className={css(s.radioItem, ...styles)} {...props}>
		<div className={css(s.radioItemCheckWrapper)}>
			<Menu.RadioItemIndicator className={css(s.radioItemCheckIndicator)}>
				<BulletIcon fillColor={brandSecondary} className={css(s.radioItemCheck)} />
			</Menu.RadioItemIndicator>
		</div>
		<span>{children}</span>
	</Menu.RadioItem>
));
DropdownRadioItem.displayName = 'DropdownRadioItem';

const DropdownSubTrigger = React.forwardRef<
	HTMLDivElement,
	AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.SubmenuTrigger>>
>(({ styles = [], children, ...props }, ref) => (
	<Menu.SubmenuTrigger ref={ref} className={css(s.subTrigger, ...styles)} {...props}>
		{children}
		<ChevronRight className={css(s.subTriggerChevron)} />
	</Menu.SubmenuTrigger>
));
DropdownSubTrigger.displayName = 'DropdownSubTrigger';

interface ComboDropdownTriggerProps extends AphroditeStyled<React.ComponentPropsWithoutRef<typeof Menu.Trigger>> {
	onPrimaryClick: () => void;
	children: React.ReactNode;
	variant?: 'primary' | 'secondary' | 'success' | 'link' | 'destructive';
	size?: 'default' | 'sm' | 'lg' | 'icon';
	disabled?: boolean;
}

const ComboDropdownTrigger = React.forwardRef<HTMLDivElement, ComboDropdownTriggerProps>(
	({ styles = [], children, onPrimaryClick, variant = 'primary', size = 'default', disabled, ...props }, ref) => (
		<div ref={ref} className={css(s.comboTriggerWrapper, ...styles)}>
			<button
				type='button'
				className={css(s.comboTriggerPrimary, s[`combo${variant}Primary`], s[`combo${size}`])}
				onClick={onPrimaryClick}
				disabled={disabled}
			>
				{children}
			</button>
			<Menu.Trigger
				className={css(s.comboTriggerDropdown, s[`combo${variant}Dropdown`], s[`combo${size}`])}
				disabled={disabled}
				{...props}
			>
				<ChevronDown className={css(s.comboTriggerChevron)} />
			</Menu.Trigger>
		</div>
	)
);
ComboDropdownTrigger.displayName = 'ComboDropdownTrigger';

export {
	ComboDropdownTrigger,
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownGroup,
	DropdownGroupLabel,
	DropdownItem,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownSelectTrigger,
	DropdownSeparator,
	DropdownSubTrigger,
	DropdownTrigger,
};
